import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * FreeMusiers cube — perspective cube + real 3D back-face culling.
 * F on −X, M on +X. Smooth yaw (music = spin rate only).
 */

type Vec3 = [number, number, number];
type Pt2 = { x: number; y: number };

type Sat = {
  phase: number;
  orbit: number;
  scale: number;
  spinDir: number;
  yaw: number;
};

const sats: Sat[] = [];
let heroYaw = 0;
let lastT = -1;
/** Smoothed kick squash envelope 0..1 */
let squashEnv = 0;
let shearEnv = 0;

const CAM_Z = -4.5; // camera on -Z looking toward origin

function V(x: -1 | 1, y: -1 | 1, z: -1 | 1): number {
  return (x > 0 ? 1 : 0) | (y > 0 ? 2 : 0) | (z > 0 ? 4 : 0);
}

/** Outward windings (CCW from outside, Y-up, right-handed). */
const FACES: { verts: [number, number, number, number]; letter?: 'F' | 'M'; shade: number }[] = [
  // +Y top
  { verts: [V(-1, 1, -1), V(-1, 1, 1), V(1, 1, 1), V(1, 1, -1)], shade: 0.52 },
  // -Y bottom
  { verts: [V(-1, -1, -1), V(1, -1, -1), V(1, -1, 1), V(-1, -1, 1)], shade: 0.26 },
  // -X F
  { verts: [V(-1, -1, -1), V(-1, -1, 1), V(-1, 1, 1), V(-1, 1, -1)], shade: 0.64, letter: 'F' },
  // +X M
  { verts: [V(1, -1, 1), V(1, -1, -1), V(1, 1, -1), V(1, 1, 1)], shade: 0.84, letter: 'M' },
  // -Z F
  { verts: [V(1, -1, -1), V(-1, -1, -1), V(-1, 1, -1), V(1, 1, -1)], shade: 0.7, letter: 'F' },
  // +Z M
  { verts: [V(-1, -1, 1), V(1, -1, 1), V(1, 1, 1), V(-1, 1, 1)], shade: 0.76, letter: 'M' },
];

function corners(): Vec3[] {
  const out: Vec3[] = new Array(8);
  for (let i = 0; i < 8; i++) {
    out[i] = [i & 1 ? 1 : -1, i & 2 ? 1 : -1, i & 4 ? 1 : -1];
  }
  return out;
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function rotY([x, y, z]: Vec3, yaw: number): Vec3 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return [x * c - z * s, y, x * s + z * c];
}
function rotX([x, y, z]: Vec3, pitch: number): Vec3 {
  const c = Math.cos(pitch);
  const s = Math.sin(pitch);
  return [x, y * c - z * s, y * s + z * c];
}

function project(v: Vec3, ox: number, oy: number, scale: number): Pt2 {
  // camera at (0,0,CAM_Z), look +Z; perspective divide
  const dz = v[2] - CAM_Z; // distance along view (~ positive)
  const k = scale * (Math.abs(CAM_Z) / Math.max(0.35, dz));
  return { x: ox + v[0] * k, y: oy - v[1] * k };
}

/** Music-driven lattice morph (model space, before rotate). */
type Morph = {
  sx: number;
  sy: number;
  sz: number;
  shearX: number; // x += shearX * y
  shearZ: number; // z += shearZ * y
  twist: number; // rad per unit y
  bulge: number; // face center push
};

function morphVertex([x, y, z]: Vec3, m: Morph): Vec3 {
  // non-uniform scale (squash / stretch)
  let mx = x * m.sx;
  let my = y * m.sy;
  let mz = z * m.sz;
  // vertical shear — parallelogram lean
  mx += m.shearX * my;
  mz += m.shearZ * my;
  // twist around Y (jelly roll)
  if (m.twist !== 0) {
    const a = m.twist * my;
    const c = Math.cos(a);
    const s = Math.sin(a);
    const rx = mx * c - mz * s;
    const rz = mx * s + mz * c;
    mx = rx;
    mz = rz;
  }
  // radial bulge on faces (breathe)
  if (m.bulge !== 0) {
    const push = 1 + m.bulge * (0.35 + 0.65 * Math.abs(my));
    mx *= push;
    my *= push * (1 - m.bulge * 0.15);
    mz *= push;
  }
  return [mx, my, mz];
}

function ensureSats(n: number): void {
  while (sats.length < n) {
    sats.push({
      phase: Math.random() * Math.PI * 2,
      orbit: 0.85 + Math.random() * 0.3,
      scale: 0.32 + Math.random() * 0.22,
      spinDir: Math.random() > 0.5 ? 1 : -1,
      yaw: Math.random() * Math.PI * 2,
    });
  }
  while (sats.length > n) sats.pop();
}

function drawSolidCube(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  size: number,
  yaw: number,
  pitch: number,
  glow: number,
  pulse: number,
  morph: Morph,
): void {
  const scale = size * (1 + pulse * 0.05);
  const world = corners().map((p) => rotX(rotY(morphVertex(p, morph), yaw), pitch));
  const screen = world.map((p) => project(p, ox, oy, scale));

  type FaceDraw = {
    pts: Pt2[];
    shade: number;
    letter?: 'F' | 'M';
    depth: number;
  };

  const visible: FaceDraw[] = [];

  for (const face of FACES) {
    const w0 = world[face.verts[0]]!;
    const w1 = world[face.verts[1]]!;
    const w2 = world[face.verts[2]]!;
    const w3 = world[face.verts[3]]!;

    // outward normal
    const n = cross(sub(w1, w0), sub(w2, w0));
    // face center
    const cx = (w0[0] + w1[0] + w2[0] + w3[0]) * 0.25;
    const cy = (w0[1] + w1[1] + w2[1] + w3[1]) * 0.25;
    const cz = (w0[2] + w1[2] + w2[2] + w3[2]) * 0.25;
    // vector from face to camera
    const toCam: Vec3 = [0 - cx, 0 - cy, CAM_Z - cz];
    if (dot(n, toCam) <= 0) continue; // back face

    const fp = face.verts.map((i) => screen[i]!);
    // depth: closer to camera = smaller dz = draw later
    const depth = cz; // larger z (toward +Z / away from cam at -Z) = farther
    visible.push({ pts: fp, shade: face.shade, letter: face.letter, depth });
  }

  // far → near (high z first when cam is at -Z)
  visible.sort((a, b) => b.depth - a.depth);

  const edgeCol = `rgba(255, 77, 109, ${0.75 + glow * 0.25})`;
  const edgeW = Math.max(1.4, size * 0.04);

  for (const f of visible) {
    path(ctx, f.pts);
    const r = 24 + f.shade * 64;
    const g = 6 + f.shade * 16;
    const b = 12 + f.shade * 24;
    ctx.fillStyle = `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${0.92 + glow * 0.05})`;
    ctx.fill();

    ctx.strokeStyle = edgeCol;
    ctx.lineWidth = edgeW;
    ctx.lineJoin = 'round';
    ctx.shadowColor = `rgba(255, 77, 109, ${0.35 + glow * 0.45})`;
    ctx.shadowBlur = 5 + glow * 12 + pulse * 6;
    ctx.stroke();
    ctx.shadowBlur = 0;

    if (f.letter) drawLetterOnFace(ctx, f.pts, f.letter, size, glow);
  }

  for (const f of visible) {
    path(ctx, f.pts);
    ctx.strokeStyle = `rgba(255, 180, 196, ${0.28 + glow * 0.25})`;
    ctx.lineWidth = Math.max(1, edgeW * 0.4);
    ctx.stroke();
  }
}

function path(ctx: CanvasRenderingContext2D, pts: Pt2[]): void {
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
}

function drawLetterOnFace(
  ctx: CanvasRenderingContext2D,
  pts: Pt2[],
  letter: string,
  size: number,
  glow: number,
): void {
  const c = {
    x: (pts[0]!.x + pts[1]!.x + pts[2]!.x + pts[3]!.x) / 4,
    y: (pts[0]!.y + pts[1]!.y + pts[2]!.y + pts[3]!.y) / 4,
  };
  const m01 = { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 };
  const m12 = { x: (pts[1]!.x + pts[2]!.x) / 2, y: (pts[1]!.y + pts[2]!.y) / 2 };
  const m23 = { x: (pts[2]!.x + pts[3]!.x) / 2, y: (pts[2]!.y + pts[3]!.y) / 2 };
  const m30 = { x: (pts[3]!.x + pts[0]!.x) / 2, y: (pts[3]!.y + pts[0]!.y) / 2 };

  let ux = (m01.x - m23.x) * 0.3;
  let uy = (m01.y - m23.y) * 0.3;
  let vx = (m12.x - m30.x) * 0.3;
  let vy = (m12.y - m30.y) * 0.3;

  if (Math.abs(uy) < Math.abs(vy)) {
    const tx = ux;
    const ty = uy;
    ux = -vx;
    uy = -vy;
    vx = tx;
    vy = ty;
  }
  if (vy > 0) {
    ux = -ux;
    uy = -uy;
    vx = -vx;
    vy = -vy;
  }

  ctx.save();
  ctx.transform(ux, uy, vx, vy, c.x, c.y);
  ctx.font = '800 1.5px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 0.14;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(20, 5, 10, 0.9)';
  ctx.strokeText(letter, 0, 0);
  ctx.fillStyle = `rgba(255, 210, 222, ${0.88 + glow * 0.12})`;
  ctx.shadowColor = 'rgba(255, 77, 109, 0.85)';
  ctx.shadowBlur = Math.max(3, size * 0.06);
  ctx.fillText(letter, 0, 0);
  ctx.restore();
  ctx.shadowBlur = 0;
}

export const cubesViz: Visualizer = {
  id: 'cubes',
  label: 'FreeMusiers Cubes',
  draw(frame) {
    const { ctx, w, h, bands, freq, t } = frame;
    clearBg(ctx, w, h, 0.55);

    let dt = 1 / 60;
    if (lastT >= 0) dt = Math.min(0.05, Math.max(0.001, t - lastT));
    lastT = t;

    const spinRate = 0.48 + bands.energy * 0.8 + bands.mids * 0.4;
    heroYaw += spinRate * dt;

    // —— rhythmic morph (not just scale) ——
    // attack fast on kick, slower release → bouncy jelly
    const kickHit = Math.max(bands.kick, bands.beat * 0.75);
    const squashT = Math.min(1, kickHit * 1.05 + bands.bass * 0.35);
    const atk = kickHit > squashEnv ? 18 : 7;
    squashEnv += (squashT - squashEnv) * Math.min(1, dt * atk);

    const shearT =
      Math.sin(t * (2.2 + bands.energy * 2.5)) * bands.mids * 0.55 +
      Math.sin(t * 5.5) * bands.highs * 0.2 +
      (bands.beat > 0.35 ? Math.sign(Math.sin(t * 9)) * bands.beat * 0.35 : 0);
    shearEnv += (shearT - shearEnv) * Math.min(1, dt * 12);

    // volume-preserving-ish squash: down on Y, out on XZ
    const sq = squashEnv;
    const heroMorph: Morph = {
      sx: 1 + sq * 0.42 + bands.energy * 0.06 + Math.sin(t * 3.1) * bands.mids * 0.08,
      sy: 1 - sq * 0.48 + bands.highs * 0.05,
      sz: 1 + sq * 0.38 + Math.cos(t * 2.7) * bands.mids * 0.08,
      shearX: shearEnv * 0.55,
      shearZ: Math.cos(t * (1.8 + bands.mids)) * bands.mids * 0.28 + bands.beat * 0.12 * Math.sin(t * 7),
      twist: bands.highs * 0.32 * Math.sin(t * (4 + bands.energy * 3)) + sq * 0.12 * Math.sin(t * 11),
      bulge: bands.energy * 0.14 + bands.mids * 0.1 + sq * 0.08,
    };

    const bg = ctx.createRadialGradient(w / 2, h * 0.45, 8, w / 2, h * 0.45, Math.min(w, h) * 0.55);
    bg.addColorStop(0, `rgba(50, 10, 22, ${0.28 + bands.energy * 0.22})`);
    bg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = 'rgba(255, 77, 109, 0.018)';
    for (let y = 0; y < h; y += 5) ctx.fillRect(0, y, w, 1);

    const nSat = Math.min(5, Math.max(0, Math.floor(bands.energy * 5 + bands.highs * 2)));
    ensureSats(nSat);

    const pitch = -0.4;

    for (let i = 0; i < nSat; i++) {
      const s = sats[i]!;
      const a = t * (0.4 + bands.energy * 0.3) + s.phase + (i / Math.max(nSat, 1)) * Math.PI * 2;
      const radius = Math.min(w, h) * (0.24 * s.orbit + bands.energy * 0.04);
      const ox = w / 2 + Math.cos(a) * radius * 1.2;
      const oy =
        h * 0.5 + Math.sin(a * 1.1) * radius * 0.3 + Math.sin(t * 2.5 + s.phase) * bands.kick * 8;
      const f = freq[(i * 13) % freq.length]! / 255;
      s.yaw += s.spinDir * dt * (0.65 + bands.mids * 0.7 + f);
      const sz = Math.min(w, h) * 0.068 * s.scale * (0.9 + f * 0.3);
      const phase = s.phase;
      const satMorph: Morph = {
        sx: 1 + sq * 0.3 + f * 0.12,
        sy: 1 - sq * 0.35 + f * 0.05,
        sz: 1 + sq * 0.28,
        shearX: shearEnv * 0.4 * Math.sin(phase),
        shearZ: shearEnv * 0.3 * Math.cos(phase),
        twist: bands.highs * 0.2 * Math.sin(t * 5 + phase),
        bulge: f * 0.12 + bands.energy * 0.06,
      };
      drawSolidCube(
        ctx,
        ox,
        oy,
        sz,
        s.yaw,
        pitch,
        0.28 + f * 0.4 + bands.beat * 0.2,
        bands.kick * 0.35 + f,
        satMorph,
      );
    }

    const heroSize = Math.min(w, h) * (0.21 + bands.energy * 0.025);
    // bounce from squash recovery (stretch rebound)
    const rebound = Math.max(0, 0.15 - sq) * bands.bass * 10;
    drawSolidCube(
      ctx,
      w / 2,
      h * 0.46 + Math.sin(t * 1.6) * bands.bass * 4 - sq * 14 + rebound,
      heroSize,
      heroYaw,
      pitch,
      0.45 + bands.energy * 0.4 + bands.highs * 0.2 + sq * 0.2,
      bands.kick * 0.5 + bands.beat * 0.35 + bands.energy * 0.15,
      heroMorph,
    );

    ctx.strokeStyle = `rgba(255, 77, 109, ${0.16 + bands.bass * 0.22})`;
    ctx.lineWidth = 2;
    ctx.shadowColor = 'rgba(255, 77, 109, 0.35)';
    ctx.shadowBlur = 8 + bands.kick * 10;
    ctx.beginPath();
    ctx.ellipse(
      w / 2,
      h * 0.72,
      Math.min(w, h) * 0.3 * (1 + bands.energy * 0.12),
      Math.min(w, h) * 0.048,
      0,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.font = `700 ${Math.max(14, Math.round(w * 0.034))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = `rgba(255, 77, 109, ${0.55 + bands.energy * 0.4})`;
    ctx.shadowColor = 'rgba(255, 77, 109, 0.75)';
    ctx.shadowBlur = 14 + bands.kick * 12;
    ctx.fillText('FREEMUSIERS.COM', w / 2, h * 0.84);
    ctx.shadowBlur = 0;

    drawHud(frame);
  },
};
